import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  DbQuestionData,
  Player,
  ReportInaccuracy,
  DetailedPlayer,
  Mode,
  WhosOnline,
  GameDataFromServer,
  Spectator,
  VanquishedDemon,
} from './types';
import { Game } from './game';
import './styles.css';
import { ScoreScreen } from './game/score-screen/ScoreScreen';
import { Socket } from 'socket.io-client';
import { useLocation, useNavigate } from 'react-router-dom';
import { Countdown } from './countdown';
import { isMobile } from 'react-device-detect';
import { Box, Typography } from '@mui/material';
import { assignRanks } from './utils';
import { PausedDialog } from '../../dialog/PausedDialog';
import { Assists, SocketCallback } from '@par/shared/common/types';
import {
  Game as GameType,
  Penalty,
  ProperNameShortAnswer,
  Team,
  Teams,
} from '@par/shared/types/game';
import { CustomQuestionTestingData } from '../custom-questions';
import { cloneDeep } from 'lodash';
import { Profile, DetailedUserAchievement } from '@par/shared/types/player';
import { StarterKitConfig } from '../starter-kits/types';
import { TournamentGame } from './game/TournamentGame';
import { ScoreScreenLoading } from './ScoreScreenLoading';
import { fetchPost } from '@par/shared/api/common';

let retryDetailedPlayersEmit;

export const sortPlayers = (
  players: Record<string, Player | DetailedPlayer>
) => {
  const sortedPlayers = Object.values(players).sort(
    (a, b) => a.parScore - b.parScore
  );
  return assignRanks(sortedPlayers);
};

type LocationState = {
  daily?: boolean;
  dailyChallengeTower?: number;
  dailyTheme?: string;
  fromMain?: boolean;
  fromSoloPunchcard?: boolean;
  fromStarterKit?: boolean;
  ghostChallenge?: boolean;
  ghostChallengeFrom?: string;
  ghostChallengeTo?: string;
  matchMaking?: boolean;
  matchMakingSeededBy?: string;
  rejoin: boolean;
  spectate: boolean;
  tournament?: number;
};

type PropTypes = {
  detailedUserAchievements?: DetailedUserAchievement[];
  customQuestionTestingData?: CustomQuestionTestingData;
  profile: Profile;
  gameDataFromServer?: GameDataFromServer;
  onCategoryBanClick?: () => void;
  handleJoinGameClick?: (gameId: string, mode: Mode) => void;
  handleLeaveGameClick?: (gameId: string, profileId: string) => void;
  lobbyOpenGames?: GameType[];
  onUpdateProfile: () => void;
  showSnackbarMessage: (message: string) => void;
  socket?: Socket;
  spectateSocket?: Socket;
  spectateGameDataFromServer?: GameDataFromServer;
  whosOnline?: WhosOnline[];
};

export const Room = ({
  detailedUserAchievements,
  customQuestionTestingData,
  profile,
  gameDataFromServer,
  handleJoinGameClick,
  handleLeaveGameClick,
  onCategoryBanClick,
  lobbyOpenGames,
  onUpdateProfile,
  showSnackbarMessage,
  socket,
  spectateSocket,
  spectateGameDataFromServer,
  whosOnline,
}: PropTypes) => {
  const initPlayerSelf: Player = {
    avatarPublicId: profile.avatarPublicId,
    avatarUrl: profile.avatarUrl,
    confirmedReady: false,
    correctAnswer: false,
    currentScore: 0,
    incorrectAnswer: false,
    fuzzyScore: null,
    isMobile: false,
    isPromptAnswer: false,
    location: profile.location,
    rankedMatchRating: profile.rankedMatchRating,
    rankedMatchGamesPlayed: profile.rankedMatchGamesPlayed,
    rankedSitAndGoRating: profile.rankedSitAndGoRating,
    rankedSitAndGoGamesPlayed: profile.rankedSitAndGoGamesPlayed,
    parScore: 0,
    profileId: profile.id,
    rank: null,
    timerLimitReached: false,
    totalPenalty: 0,
    username: profile.username,
  };

  const navigate = useNavigate();
  const location = useLocation();
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [allListenersActive, setAllListenersActive] = useState({
    socket: false,
  });
  const locationState = location.state as LocationState;
  const gameId = customQuestionTestingData
    ? customQuestionTestingData.gameId
    : locationState?.spectate && spectateGameDataFromServer
    ? spectateGameDataFromServer.gameId
    : gameDataFromServer?.gameId;
  const [mode, setMode] = useState<Mode>(
    locationState?.spectate && spectateGameDataFromServer
      ? spectateGameDataFromServer.mode
      : gameDataFromServer?.mode
  );
  const [league, setLeague] = useState<boolean>(
    locationState?.spectate && spectateGameDataFromServer
      ? spectateGameDataFromServer.league
      : gameDataFromServer?.league
  );
  const gameType =
    locationState?.spectate && spectateGameDataFromServer
      ? spectateGameDataFromServer.gameType
      : gameDataFromServer?.gameType;
  const ranked =
    locationState?.spectate && spectateGameDataFromServer
      ? spectateGameDataFromServer.ranked
      : gameDataFromServer?.ranked;
  const categorySelections =
    locationState?.spectate && spectateGameDataFromServer
      ? spectateGameDataFromServer.categorySelections
      : gameDataFromServer?.categorySelections;
  const categorySet =
    locationState?.spectate && spectateGameDataFromServer
      ? spectateGameDataFromServer.categorySet
      : gameDataFromServer?.categorySet;
  const [playerSelf, setPlayerSelf] = useState<Player>(
    !locationState?.spectate ? initPlayerSelf : undefined
  );
  const [players, setPlayers] = useState<Record<string, Player>>(
    !locationState?.spectate
      ? {
          [profile.id]: initPlayerSelf,
        }
      : undefined
  );
  const [teams, setTeams] = useState<Teams>();
  const [myTeamId, setMyTeamId] = useState(gameDataFromServer?.team);
  const [myTeam, setMyTeam] = useState<Team>();
  const [otherTeam, setOtherTeam] = useState<Team>();
  const [detailedPlayers, setDetailedPlayers] =
    useState<Record<string, DetailedPlayer>>();
  const [questionSet, setQuestionSet] = useState<DbQuestionData[]>();
  const [gameStarted, setGameStarted] = useState(false);
  const [countdown, setCountdown] = useState<number>();
  const [questionIndex, setQuestionIndex] = useState<number>();
  const [gameClock, setGameClock] = useState(0);
  const [freezeGameClock, setFreezeGameClock] = useState(false);
  const [revealQuestion, setRevealQuestion] = useState('');
  const [revealFullQuestion, setRevealFullQuestion] = useState<string>();
  const [questionMetadata, setQuestionMetadata] =
    useState<Record<string, any>>();
  const [revealAnswer, setRevealAnswer] = useState<string[][]>();
  const [timerLimitReached, setTimerLimitReached] = useState(false);
  const [gameOver, setGameOver] = useState(false);
  const [roundOver, setRoundOver] = useState(false);
  const [unrevealedIndex, setUnrevealedIndex] = useState<number[]>();
  const [fuzzy, setFuzzy] = useState<Record<string, number>>();
  const [playerThem, setPlayerThem] = useState<Player>();
  const [properNameShortAnswer, setProperNameShortAnswer] =
    useState<ProperNameShortAnswer>();
  const [reportInaccuracy, setReportInaccuracy] =
    useState<ReportInaccuracy[]>();
  const [penalty, setPenalty] = useState<Penalty>();
  const [spectators, setSpectators] = useState<Spectator[] | number>([]);
  const [spectatorList, setSpectatorList] = useState<Spectator[]>();
  const [spectatorCount, setSpectatorCount] = useState<number>();
  const [delayBeforeQuestionRevealCount, setDelayBeforeQuestionRevealCount] =
    useState(2);
  const [nextQuestionDelayCount, setNextQuestionDelayCount] = useState(4);
  const [selectedPlayer, setSelectedPlayer] = useState<DetailedPlayer>();
  const [nextRoundStartTime, setNextRoundStartTime] = useState<number>();
  // use this for a bracket in a modal
  const [bracketMatches, setBracketMatches] =
    useState<Record<string, { usernames: string[] }>>();
  const [disconnectedPlayers, setDisconnectedPlayers] = useState<string[]>([]);
  const [mobilePlayers, setMobilePlayers] = useState<string[]>([]);
  const [averageDifficulty, setAverageDifficulty] = useState<number>();
  const [isWindowTooSmall, setIsWindowTooSmall] = useState<boolean>(false);
  const [paused, setPaused] = useState(false);
  const [willPause, setWillPause] = useState(false);
  const [unpauseCount, setUnpauseCount] = useState<number>();
  const [cutoff, setCutoff] = useState<number>();
  const [bannedCategories, setBannedCategories] = useState<string[]>();
  const [mathFormula, setMathFormula] = useState<string>();
  const [seedDetails, setSeedDetails] = useState<{
    submittedBy: string;
    submittedByRating: number;
  }>();
  const [handicap, setHandicap] = useState<number>();
  const [questionImgUrl, setQuestionImgUrl] = useState<string>();
  const [preventReset, setPreventReset] = useState(false); // when a player rejoins, some useEffect dependencies might trigger mid-game undesirably
  const [tournament, setTournament] = useState(
    locationState?.spectate && spectateGameDataFromServer
      ? spectateGameDataFromServer.tournament
      : gameDataFromServer?.tournament
  );
  const [variant, setVariant] = useState(
    locationState?.spectate && spectateGameDataFromServer
      ? spectateGameDataFromServer.variant
      : gameDataFromServer?.variant || 'normal'
  );
  const [survivalMode, setSurvivalMode] = useState(
    locationState?.spectate && spectateGameDataFromServer
      ? spectateGameDataFromServer.survivalMode
      : gameDataFromServer?.survivalMode
  );
  const [demons, setDemons] = useState(
    locationState?.spectate && spectateGameDataFromServer
      ? spectateGameDataFromServer.demons
      : gameDataFromServer?.demons
  );
  const [demo, setDemo] = useState(
    locationState?.spectate && spectateGameDataFromServer
      ? spectateGameDataFromServer.demo
      : gameDataFromServer?.demo
  );
  const [survivalChallengeTimerLimit, setSurvivalChallengeTimerLimit] =
    useState(20);
  const [
    survivalChallengePhaseTwoChallenge,
    setSurvivalChallengePhaseTwoChallenge,
  ] = useState('none');
  const [lifes, setLifes] = useState<number>(0);
  const [survivalChallengeStatuses, setSurvivalChallengeStatuses] = useState<{
    firstChallengeMet: boolean;
    secondChallengeMet: boolean;
    thirdChallengeMet: boolean;
  }>();
  const [fastNext, setFastNext] = useState(
    locationState?.spectate && spectateGameDataFromServer
      ? spectateGameDataFromServer.fastNext
      : gameDataFromServer?.fastNext
  );
  const [demonsVanquished, setDemonsVanquished] = useState<VanquishedDemon[]>();
  const [starterKit, setStarterKit] = useState<StarterKitConfig>(
    locationState?.spectate && spectateGameDataFromServer
      ? spectateGameDataFromServer.starterKit
      : gameDataFromServer?.starterKit
  );
  const [tournamentTotalPlayers, setTournamentTotalPlayers] =
    useState<number>();
  const [assists, setAssists] = useState<Assists>();

  const countdownRef = useRef<number>();
  const questionIndexRef = useRef<number>();
  /** Only reset per-question state when the index actually changes (not when `resetStatesAndPlayers` is recreated on every `players` update). */
  const prevQuestionIndexForResetRef = useRef<number | undefined>(undefined);
  const questionQueueRef = useRef<string[]>([]);
  const questionResetAfterDisconnect = useRef(true);
  const revealSetTimeoutRef = useRef<any>();
  const playersRef = useRef(players);
  const hasRequestedDetailedPlayersRef = useRef(false);
  const [connectCounter, setConnectCounter] = useState(0);
  const [disconnectCounter, setDisconnectCounter] = useState(0);

  const handleConnect = () => {
    setConnectCounter((prev) => prev + 1);
  };

  const handleDisconnect = () => {
    setDisconnectCounter((prev) => prev + 1);
  };

  const resetDisconnectCounterInLocalStorage = () => {
    // localStorage.setItem('par_current_game_disconnects', null);
  };

  const requestRejoinData = useCallback(() => {
    if (isOnline && socket) {
      try {
        socket.emit('rejoinGameRequest', {
          profileId: profile.id,
          gameId: gameId,
        });
      } catch (error) {
        console.error('Rejoin data fetch failed', error);
      }
    } else {
      console.log('Browser is offline, retrying...');
      setTimeout(requestRejoinData, 3000); // Retry after 3 seconds
    }
  }, [isOnline, socket, profile.id, gameId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    console.log('INIT ROOM!');
    if (customQuestionTestingData) {
      const { countdown, gameStarted } = customQuestionTestingData;
      setGameStarted(gameStarted);
      setCountdown(countdown);
      return;
    }

    // sometimes a race condition, lag spike, or some other interruption
    // might cause a situation where the game doesnt render and shows a blank screen
    // This settimeout just checks if after 5 seconds, some critical states aren't updated
    // assume that something went wrong and try rejoining
    const rejoinFallbackId = setTimeout(() => {
      if (
        countdownRef.current === undefined &&
        questionIndexRef.current === undefined
      ) {
        navigate('/loading', {
          state: { rejoin: true },
        });
      }
    }, 5000);

    const goOnline = () => {
      console.log('GO ONLINE');
      setIsOnline(true);
      requestRejoinData();
    };

    const goOffline = () => {
      setIsOnline(false);
    };

    window.history.pushState(null, '', window.location.pathname);
    window.addEventListener('beforeunload', () => stopSpectating(false));
    window.addEventListener('popstate', () => stopSpectating(true));
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    return () => {
      clearTimeout(rejoinFallbackId);
      window.removeEventListener('beforeunload', () => stopSpectating(false));
      window.removeEventListener('popstate', () => stopSpectating(true));
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
      socket.off('pong');
      socket.off('players');
      socket.off('playerUpdate');
      socket.off('detailedPlayers');
      socket.off('gameStarted');
      socket.off('countdown');
      socket.off('questionIndex');
      socket.off('questionMetadata');
      socket.off('gameClock');
      socket.off('revealAnswer');
      socket.off('revealQuestion');
      socket.off('revealFullQuestion');
      socket.off('timerLimitReached');
      socket.off('unrevealedIndex');
      socket.off('gameOver');
      socket.off('roundOver');
      socket.off('questionSet');
      socket.off('properNameShortAnswer');
      socket.off('reportInaccuracy');
      socket.off('preventAnswers');
      socket.off('spectators');
      socket.off('bannedCategories');
      socket.off('delayBeforeQuestionRevealCount');
      socket.off('nextQuestionDelayCount');
      socket.off('nextRoundStartTime');
      socket.off('averageDifficulty');
      socket.off('paused');
      socket.off('willPause');
      socket.off('unpauseCount');
      socket.off('cutoff');
      socket.off('bracketMatches');
      socket.off('survivalChallengeTimerLimit');
      socket.off('survivalChallengePhaseTwoChallenge');
      socket.off('survivalChallengeLivesRemaining');
      socket.off('demonsVanquished');
      socket.off('tournamentTotalPlayers');
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- intentional mount-only: init effect registers window listeners and socket cleanup once; adding deps would re-register on every render

  useEffect(() => {
    if (!connectCounter) return;
    requestRejoinData();
  }, [connectCounter, requestRejoinData]);

  useEffect(() => {
    questionResetAfterDisconnect.current = false;
    // const localStorageDisconnectsCounter = localStorage.getItem(
    //   'par_current_game_disconnects'
    // );
    // const parsed = JSON.parse(localStorageDisconnectsCounter);
    // localStorage.setItem('par_current_game_disconnects', parsed + 1);
  }, [disconnectCounter]);

  useEffect(() => {
    countdownRef.current = countdown;
    questionIndexRef.current = questionIndex;
  }, [countdown, questionIndex]);

  useEffect(() => {
    if (!gameDataFromServer) return;
    setMode(gameDataFromServer.mode);
  }, [gameDataFromServer]);

  useEffect(() => {
    // this will make sure the user is in the room and listening to all sockets
    // before letting the server know to start the game
    if (allListenersActive.socket) {
      if (
        !locationState?.rejoin &&
        !locationState?.spectate &&
        !locationState.tournament &&
        mode !== 'teams'
      ) {
        socket.emit('confirmedInRoom', {
          gameId: gameId,
          profileId: profile.id,
        });
        resetDisconnectCounterInLocalStorage();
      }
    }
  }, [allListenersActive]); // eslint-disable-line react-hooks/exhaustive-deps -- intentional: must fire exactly once when listeners become active; adding mode/socket/etc. would re-emit confirmedInRoom on every state change

  useEffect(() => {
    playersRef.current = players;
  }, [players]);

  useEffect(() => {
    if (!gameStarted || locationState?.spectate || locationState?.rejoin) return;
    const timer = setTimeout(() => {
      const currentPlayers = playersRef.current;
      const playerCount = currentPlayers ? Object.keys(currentPlayers).length : 0;
      if (playerCount < 2) {
        console.warn('[room] players incomplete after gameStarted, requesting rejoin data');
        requestRejoinData();
      }
    }, 2000);
    return () => clearTimeout(timer);
  }, [gameStarted]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const activeSocket = locationState?.spectate && spectateSocket ? spectateSocket : socket;
    if (!activeSocket) return;
    // listeners are assigned once socket is connected instead of on component mount
    // because the socket is optional in the room.tsx component to allow it it disconnect and reconnect
    // during a game, otherwise the player would be kicked out of the room page.
    // I'm sure there is a better way to do this
    activeSocket.on('teams', setTeams);
    activeSocket.on('players', setPlayers);
    activeSocket.on('playerUpdate', updatePlayer);
    activeSocket.on('detailedPlayers', updateDetailedPlayers);
    activeSocket.on('fuzzy', updateFuzzy);
    activeSocket.on('penalty', setPenalty);
    activeSocket.on('assists', setAssists);
    activeSocket.on('survivalChallengeStatuses', setSurvivalChallengeStatuses);
    activeSocket.on('gameStarted', (value: boolean) => {
      setGameStarted(value);
      if (value === false) {
        navigate('/');
      }
    });
    activeSocket.on('countdown', setCountdown);
    activeSocket.on('questionIndex', setQuestionIndex);
    activeSocket.on('questionMetadata', setQuestionMetadata);
    activeSocket.on('gameClock', setGameClock);
    activeSocket.on('revealAnswer', setRevealAnswer);
    activeSocket.on('revealQuestion', updateQuestion);
    activeSocket.on('revealFullQuestion', updateFullQuestionReveal);
    activeSocket.on('timerLimitReached', setTimerLimitReached);
    activeSocket.on('unrevealedIndex', setUnrevealedIndex);
    activeSocket.on('gameOver', setGameOver);
    activeSocket.on('roundOver', setRoundOver);
    activeSocket.on('questionSet', setQuestionSet);
    activeSocket.on('properNameShortAnswer', setProperNameShortAnswer);
    activeSocket.on('reportInaccuracy', setReportInaccuracy);
    activeSocket.on('bannedCategories', setBannedCategories);
    activeSocket.on(
      'delayBeforeQuestionRevealCount',
      setDelayBeforeQuestionRevealCount
    );
    activeSocket.on('nextQuestionDelayCount', setNextQuestionDelayCount);
    activeSocket.on('nextRoundStartTime', setNextRoundStartTime);
    activeSocket.on('averageDifficulty', setAverageDifficulty);
    activeSocket.on('paused', setPaused);
    activeSocket.on('willPause', setWillPause);
    activeSocket.on('unpauseCount', setUnpauseCount);
    activeSocket.on('cutoff', setCutoff);
    activeSocket.on('spectators', setSpectators);
    activeSocket.on('handicap', setHandicap);
    activeSocket.on('rejoinedData', handleRejoinedData);
    activeSocket.on('survivalChallengeTimerLimit', setSurvivalChallengeTimerLimit);
    activeSocket.on(
      'survivalChallengePhaseTwoChallenge',
      setSurvivalChallengePhaseTwoChallenge
    );
    activeSocket.on('survivalChallengeLivesRemaining', setLifes);
    activeSocket.on('demonsVanquished', setDemonsVanquished);
    activeSocket.on('tournamentTotalPlayers', setTournamentTotalPlayers);
    activeSocket.on('connect', handleConnect);
    activeSocket.on('disconnect', handleDisconnect);

    if (locationState?.spectate) {
      activeSocket.on('league', setLeague);
      activeSocket.on('spectateData', handleSpectateData);
    }

    setAllListenersActive((prev) => ({
      ...prev,
      socket: true,
    }));

    // ensure all listeners are on before starting game
    if (
      locationState?.rejoin ||
      (questionIndex !== undefined && gameClock > 0)
    ) {
      setPreventReset(true);
      clearTimeout(revealSetTimeoutRef.current);
      questionResetAfterDisconnect.current = false;
      questionQueueRef.current = [];
      activeSocket.emit('rejoinGameRequest', {
        profileId: profile.id,
        gameId: gameId,
      });
    }

    if (locationState?.spectate) {
      clearTimeout(revealSetTimeoutRef.current);
      questionResetAfterDisconnect.current = false;
      questionQueueRef.current = [];
      activeSocket.emit('spectateGameRequest', {
        profile: profile,
        gameId: gameId,
      });
    }
  }, [socket, spectateSocket]); // eslint-disable-line react-hooks/exhaustive-deps -- intentional: socket listener registration must run only when the socket reference changes, not on every render

  useEffect(() => {
    if (!spectateSocket || !spectateGameDataFromServer) return;
    if (spectateGameDataFromServer.mode !== 'teams') return;
    const gId = spectateGameDataFromServer.gameId;
    spectateSocket.emit('joinTeamSpectate', { profileId: profile?.id, gameId: gId, team: 'teamA' });
    spectateSocket.emit('joinTeamSpectate', { profileId: profile?.id, gameId: gId, team: 'teamB' });
  }, [spectateGameDataFromServer, spectateSocket, profile?.id]);


  const handleRejoinedData = (data: {
    mode: any;
    cutoff: any;
    countdown: any;
    questionMetadata: any;
    questionIndex: any;
    gameStarted: any;
    gameOver: any;
    league: any;
    revealQuestion: any;
    revealAnswer: any;
    unrevealedIndex: any;
    timerLimitReached: any;
    delayBeforeQuestionRevealCount: any;
    bannedCategories?: any;
    variant: any;
    survivalMode: any;
    survivalChallengeLivesRemaining: any;
    survivalChallengeTimerLimit: any;
    survivalChallengePhaseTwoChallenge: any;
    fastNext: any;
    teams: any;
    tournament: any;
    tournamentTotalPlayers: any;
    starterKit: any;
  }) => {
    // console.log('rejoin', data);
    setMode(data.mode);
    setCutoff(data.cutoff);
    setCountdown(data.countdown);
    setQuestionMetadata(data.questionMetadata);
    setQuestionIndex(data.questionIndex);
    setGameStarted(data.gameStarted);
    setGameOver(data.gameOver);
    updateQuestion(data.revealQuestion);
    setRevealAnswer(data.revealAnswer);
    setUnrevealedIndex(data.unrevealedIndex);
    setTimerLimitReached(data.timerLimitReached);
    setDelayBeforeQuestionRevealCount(data.delayBeforeQuestionRevealCount);
    setSurvivalMode(data.survivalMode);
    setVariant(data.variant);
    setLifes(data.survivalChallengeLivesRemaining);
    setSurvivalChallengeTimerLimit(data.survivalChallengeTimerLimit);
    setSurvivalChallengePhaseTwoChallenge(
      data.survivalChallengePhaseTwoChallenge
    );
    if (data.bannedCategories) {
      setBannedCategories(data.bannedCategories);
    }
    setFastNext(data.fastNext);
    setTeams(data.teams);
    if (data.teams) {
      const myTeamId = data.teams.teamA.members.includes(profile.id)
        ? 'teamA'
        : 'teamB';
      setMyTeamId(myTeamId);
    }
    setTournament(data.tournament);
    setTournamentTotalPlayers(data.tournamentTotalPlayers);
    setStarterKit(data.starterKit);
    setLeague(data.league);
  };

  const handleSpectateData = (data: {
    bannedCategories: any;
    countdown: any;
    cutoff: any;
    gameClock: any;
    gameOver: any;
    gameStarted: any;
    league: any;
    mode: any;
    players: any;
    questionIndex: any;
    questionMetadata: any;
    revealAnswer: any;
    revealQuestion: any;
    spectators: any;
    survivalChallengeLivesRemaining: any;
    survivalChallengePhaseTwoChallenge: any;
    survivalChallengeTimerLimit: any;
    teams: any;
    tournamentTotalPlayers: any;
    unrevealedIndex: any;
  }) => {
    setSpectators(data.spectators);
    setCutoff(data.cutoff);
    setCountdown(data.countdown);
    setQuestionMetadata(data.questionMetadata);
    setQuestionIndex(data.questionIndex);
    setPlayers(data.players);
    setGameStarted(data.gameStarted);
    setGameOver(data.gameOver);
    updateQuestion(data.revealQuestion);
    setUnrevealedIndex(data.unrevealedIndex);
    setBannedCategories(data.bannedCategories);
    setLeague(data.league);
    setLifes(data.survivalChallengeLivesRemaining);
    setMode(data.mode);
    setSurvivalChallengeTimerLimit(data.survivalChallengeTimerLimit);
    setSurvivalChallengePhaseTwoChallenge(
      data.survivalChallengePhaseTwoChallenge
    );
    if (data.teams) {
      const myTeamId = data.teams.teamA.members.includes(profile.id)
        ? 'teamA'
        : 'teamB';
      setMyTeamId(myTeamId);
    }
    setTeams(data.teams);
    setTournamentTotalPlayers(data.tournamentTotalPlayers);
  };

  const updateQuestion = (
    data:
      | {
          revealQuestion?: string;
          question?: string;
          mathFormula?: string;
          imgUrl?: string;
        }
      | string
      | null
  ) => {
    if (data == null) return;
    if (typeof data === 'string') {
      setRevealQuestion(data);
      return;
    }
    const text =
      typeof data.revealQuestion === 'string'
        ? data.revealQuestion
        : typeof data.question === 'string'
        ? data.question
        : '';
    setRevealQuestion(text);
    setMathFormula((prev) => data.mathFormula);
    setQuestionImgUrl((prev) => data.imgUrl);
  };

  const updateFullQuestionReveal = (data: {
    revealQuestion: string;
    mathFormula?: string;
    imgUrl?: string;
  }) => {
    setRevealFullQuestion(data.revealQuestion);
    if (data.mathFormula) {
      setMathFormula(data.mathFormula);
    }
    if (data.imgUrl) {
      setQuestionImgUrl(data.imgUrl);
    }
  };

  const updateDetailedPlayers = (data: {
    detailedPlayers: Record<string, DetailedPlayer>;
    seedDetails?: {
      submittedBy: string;
      submittedByRating: number;
    };
  }) => {
    clearTimeout(retryDetailedPlayersEmit);
    setDetailedPlayers(data.detailedPlayers);
    if (data.seedDetails) {
      setSeedDetails(data.seedDetails);
    }
  };

  const setupPlayersAsSpectator = (
    mode: Mode,
    players: Record<string, Player>
  ) => {
    if (mode === 'solo') setPlayerSelf(Object.values(players)[0]);
    if (mode === 'match') {
      setPlayerSelf(Object.values(players)[0]);
      setPlayerThem(Object.values(players)[1]);
    }
  };

  const setupTeamsAsSpectator = useCallback((teams: Teams) => {
    const myTeamId = teams.teamA.members.includes(profile.id)
      ? 'teamA'
      : 'teamB';
    setMyTeamId(myTeamId);
    setMyTeam(teams[myTeamId]);
    const otherTeamId = myTeamId === 'teamA' ? 'teamB' : 'teamA';
    setOtherTeam(teams[otherTeamId]);
  }, [profile.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // console.log('Window size changed', window);
    if (isMobile) {
      setIsWindowTooSmall(window.innerHeight < 500);
    }
  }, [window.innerHeight]); // eslint-disable-line react-hooks/exhaustive-deps -- window.innerHeight is not a reactive dep; a resize listener would be the proper fix but is out of scope here

  useEffect(() => {
    if (!profile || !players) return;
    // console.log('players', players);

    // // handle disconnection statuses
    // for (const key of Object.keys(players)) {
    //   if (disconnectedPlayers.includes(players[key].profileId)) {
    //     players[key].disconnected = true;
    //   } else {
    //     players[key].disconnected = false;
    //   }
    //   if (mobilePlayers.includes(players[key].profileId)) {
    //     players[key].isMobile = true;
    //   }
    // }

    // spectator set up
    if (locationState?.spectate && !Object.keys(players).includes(profile.id)) {
      if (mode === 'teams' && teams) {
        setupTeamsAsSpectator(teams);
      } else {
        setupPlayersAsSpectator(mode, players);
      }
      return;
    }

    setPlayerSelf(players[profile.id]);
    if (mode === 'match') {
      const playerThemProfileId = Object.keys(players).find(
        (profileId) => profileId !== profile.id
      );
      const them = playerThemProfileId
        ? players[playerThemProfileId]
        : undefined;
      setPlayerThem(them);
    }
  }, [players, disconnectedPlayers, mode, profile, locationState?.spectate, setupTeamsAsSpectator, teams]);

  useEffect(() => {
    if (!detailedPlayers || !detailedPlayers[profile.id]) return;
    setSelectedPlayer(detailedPlayers[profile.id]);
    clearTimeout(retryDetailedPlayersEmit);
  }, [detailedPlayers, profile.id]);

  const updatePlayer = (player: Player) => {
    if (!player) return;
    setPlayers((prev) => ({
      ...prev,
      [player.profileId]: player,
    }));
  };

  useEffect(() => {
    if (!customQuestionTestingData) return;
    const {
      countdown,
      delayBeforeQuestionRevealCount,
      gameOver,
      gameStarted,
      questionIndex,
      questionMetadata,
      gameClock,
      players,
      revealAnswer,
      revealQuestion,
      roundOver,
    } = customQuestionTestingData;
    setDelayBeforeQuestionRevealCount(delayBeforeQuestionRevealCount);
    setPlayers(players);
    setGameStarted(gameStarted);
    setCountdown(countdown);
    setQuestionIndex(questionIndex);
    setQuestionMetadata(questionMetadata);
    setGameClock(gameClock);
    setGameOver(gameOver);
    setRoundOver(roundOver);
    setRevealAnswer(revealAnswer);
    setRevealQuestion(revealQuestion);
  }, [customQuestionTestingData]);

  useEffect(() => {
    if (!playerSelf) return;
    if (playerSelf.correctAnswer) {
      setFreezeGameClock(true);
      // if (playerMe.roundScore[questionIndex || 0]?.score < 0) {
      // eagleSound.volume = 0.;
      // eagleSound.play();
      // }
    }
    if (!playerSelf?.correctAnswer) {
      setFreezeGameClock(false);
    }
  }, [playerSelf]);

  useEffect(() => {
    if (!myTeam) return;
    if (myTeam.correctAnswer) {
      setFreezeGameClock(true);
    }
    if (!myTeam?.correctAnswer) {
      setFreezeGameClock(false);
    }
  }, [myTeam]);

  const resetStatesAndPlayers = useCallback(() => {
    setFuzzy(undefined);
    setRevealQuestion('');
    setRevealFullQuestion(undefined);
    questionQueueRef.current = [];
    setMathFormula(undefined);
    setQuestionImgUrl(undefined);
    setTimerLimitReached(false);
    setAssists(undefined);
    if (players) {
      const resetPlayers = cloneDeep(players);
      for (const profileId of Object.keys(players)) {
        resetPlayers[profileId].correctAnswer = false;
        resetPlayers[profileId].incorrectAnswer = false;
        resetPlayers[profileId].isPromptAnswer = false;
        resetPlayers[profileId].timerLimitReached = false;
        resetPlayers[profileId].fuzzyScore = null;
        resetPlayers[profileId].currentScore = null;
      }
      setPlayers(resetPlayers);
    }
    if (mode === 'teams' && teams) {
      const resetTeams = cloneDeep(teams);
      for (const teamId of Object.keys(resetTeams)) {
        resetTeams[teamId].correctAnswer = false;
        resetTeams[teamId].correctAnswerPlayerUsername = null;
        resetTeams[teamId].incorrectAnswer = false;
        resetTeams[teamId].timerLimitReached = false;
        resetTeams[teamId].fuzzyScore = null;
        resetTeams[teamId].currentScore = null;
      }
      setTeams(resetTeams);
    }
  }, [players, mode, teams]);

  // at the start of every question, check some known state issues that are
  // usually caused by a momentary disconnect or other socket issues with the server
  const checkAbnormalGameStates = useCallback(() => {
    if (mode === 'sit-n-go') {
      if (Object.keys(players).length < 3) {
        requestRejoinData();
      }
    }
  }, [mode, players, requestRejoinData]);

  useEffect(() => {
    if (!teams) return;
    if (locationState?.spectate) {
      setupTeamsAsSpectator(teams);
      return;
    }
    const myTeamId = teams.teamA.members.includes(profile.id)
      ? 'teamA'
      : 'teamB';
    setMyTeamId(myTeamId);
    setMyTeam(teams[myTeamId]);
    const otherTeamId = myTeamId === 'teamA' ? 'teamB' : 'teamA';
    setOtherTeam(teams[otherTeamId]);
  }, [teams, locationState?.spectate, profile.id, setupTeamsAsSpectator]);

  useEffect(() => {
    if (questionIndex === undefined || questionIndex < 0) {
      prevQuestionIndexForResetRef.current = undefined;
      return;
    }
    if (prevQuestionIndexForResetRef.current === questionIndex) return;
    prevQuestionIndexForResetRef.current = questionIndex;

    if (preventReset) {
      setPreventReset(false);
      return;
    }
    resetStatesAndPlayers();
    checkAbnormalGameStates();
  }, [questionIndex, preventReset, resetStatesAndPlayers, checkAbnormalGameStates]);

  useEffect(() => {
    if (revealQuestion) {
      setDelayBeforeQuestionRevealCount(2);
      setNextQuestionDelayCount(5);
    }
  }, [revealQuestion]);

  useEffect(() => {
    if (!gameOver) {
      hasRequestedDetailedPlayersRef.current = false;
    }
  }, [gameOver]);

  useEffect(() => {
    if (!gameOver || !players || !socket) return;
    if (hasRequestedDetailedPlayersRef.current) return;
    hasRequestedDetailedPlayersRef.current = true;
    resetStatesAndPlayers();
    socket.emit('requestDetailedPlayers', {
      gameId,
      profileId: profile.id,
      playerProfileIds: Object.keys(players),
    });

    retryDetailedPlayersEmit = setTimeout(() => {
      socket.emit('requestDetailedPlayers', {
        gameId,
        profileId: profile.id,
        playerProfileIds: Object.keys(players),
      });
    }, 3000);
  }, [gameOver, gameId, players, profile.id, resetStatesAndPlayers, socket]);

  useEffect(() => {
    // console.log('=====================');
    // console.log('questionMetadata', questionMetadata);
    // console.log('questionIndex', questionIndex);
    // console.log('countdown', countdown);
    // console.log('profile', profile);
    // console.log('players', players);
    // console.log('playerSelf', playerSelf);
    // console.log('gameStarted', gameStarted);
    // console.log('gameOver', gameOver);
    // console.log('roundOver', roundOver);
    // console.log('nextRoundStartTime', nextRoundStartTime);
    // console.log('=====================');
  }, [
    questionMetadata,
    questionIndex,
    countdown,
    profile,
    playerSelf,
    players,
    gameStarted,
    gameOver,
    roundOver,
    delayBeforeQuestionRevealCount,
    nextRoundStartTime,
    revealAnswer,
    revealQuestion,
  ]);

  // useEffect(() => {
  //   // check if any players disconnected
  //   if (!players || !whosOnline) return;
  //   const connectedPlayers = whosOnline
  //     .filter((p) => p.online)
  //     .map((p) => p.profileId);
  //   const mobilePlayers = whosOnline
  //     .filter((p) => p.isMobile)
  //     .map((p) => p.profileId);
  //   const dc = [];
  //   const mobile = [];
  //   for (const key of Object.keys(players)) {
  //     if (!connectedPlayers.includes(players[key].profileId)) {
  //       dc.push(players[key].profileId);
  //     }
  //     if (mobilePlayers.includes(players[key].profileId)) {
  //       mobile.push(players[key].profileId);
  //     }
  //   }
  //   setDisconnectedPlayers(dc);
  //   setMobilePlayers(mobile);
  // }, [whosOnline]);

  useEffect(() => {
    if (willPause) {
      setUnpauseCount(undefined);
    }
  }, [willPause]);

  useEffect(() => {
    if (!spectators) return;
    if (typeof spectators === 'number') {
      setSpectatorCount(spectators);
      setSpectatorList(undefined);
      return;
    }
    if (Array.isArray(spectators)) {
      setSpectatorList(spectators);
      setSpectatorCount(undefined);
      return;
    }
  }, [spectators]);

  const updateFuzzy = (fuzzy: Record<string, number>) => {
    setFuzzy((prev: any) => ({
      ...prev,
      ...fuzzy,
    }));
  };

  const stopSpectating = (isPopState: boolean) => {
    const activeSocket = spectateSocket || socket;
    activeSocket?.emit('stopSpectating', {
      profileId: profile.id,
      gameId: gameId,
    });
    spectateSocket?.disconnect();
    if (isPopState) navigate('/'); // this is so bad, how do we know the main is where the user wants to go, find a better way
  };

  const handlePoolLeaderboardUsernameClick = (profileId: string) => {
    if (detailedPlayers) setSelectedPlayer(detailedPlayers[profileId]);
  };

  const handlePause = () => {
    setUnpauseCount(undefined);
    socket.emit('pause', {
      gameId: gameId,
      profileId: profile.id,
    });
  };

  const abortGame = () => {
    if (mode !== 'solo') return;
    socket.emit(
      'abortGame',
      {
        profileId: profile.id,
        gameId: gameId,
      },
      (res: SocketCallback) => {
        if (res.status === 'ok') {
          showSnackbarMessage('Solo game aborted');
          navigate('/lobby');
        }
      }
    );
  };

  const handleDisplaySizeChange = async (
    e: any,
    displaySize: 'sm' | 'md' | 'lg'
  ) => {
    const updatedProfile = {
      ...profile,
      settings: { ...profile.settings, displaySize },
    };
    await fetchPost('/profile/settings', updatedProfile);
    onUpdateProfile();
  };

  // FIX below debacle with all these conditional checks
  return (
    <>
      <PausedDialog
        isAdmin={profile.admin}
        mode={mode}
        open={paused}
        spectate={locationState?.spectate}
        unpause={handlePause}
        unpauseCount={unpauseCount}
      />
      {!isWindowTooSmall || locationState?.spectate ? (
        <div className="room">
          {countdown === undefined ? (
            <div className="game-starting-msg ">Game starting...</div>
          ) : null}
          {countdown > 0 ? (
            <Countdown
              abortGame={abortGame}
              countdown={countdown}
              isDaily={locationState?.daily || false}
              mode={mode}
            ></Countdown>
          ) : null}
          {gameId &&
          questionMetadata &&
          questionIndex !== undefined &&
          countdown === 0 &&
          profile &&
          players &&
          !gameOver &&
          !roundOver &&
          (!tournament || (tournament && mode !== 'sit-n-go')) ? (
            <Game
              answer={revealAnswer}
              assists={assists}
              bannedCategories={bannedCategories}
              category={questionMetadata.category}
              categorySelections={categorySelections}
              categorySet={categorySet}
              clearAfterPenalty={profile.settings?.clearAfterPenalty}
              cutoff={cutoff}
              customQuestionTestingData={customQuestionTestingData}
              daily={locationState?.daily || false}
              dailyChallengeTower={locationState?.dailyChallengeTower}
              dailyTheme={locationState?.dailyTheme}
              delayBeforeQuestionRevealCount={delayBeforeQuestionRevealCount}
              demo={demo}
              demons={demons}
              displaySize={profile.settings?.displaySize}
              fastNext={fastNext}
              freezeGameClock={freezeGameClock}
              fullQuestion={revealFullQuestion}
              fuzzy={fuzzy}
              gameClock={gameClock}
              gameId={gameId}
              gameOver={gameOver}
              ghostChallenge={locationState?.ghostChallenge}
              ghostChallengeFrom={locationState?.ghostChallengeFrom}
              ghostChallengeTo={locationState?.ghostChallengeTo}
              gameType={gameType}
              handleDisplaySizeChange={handleDisplaySizeChange}
              handlePause={handlePause}
              isAdmin={profile.admin}
              league={league}
              lifes={lifes}
              matchMaking={locationState?.matchMaking}
              matchMakingSeededBy={locationState?.matchMakingSeededBy}
              mathFormula={mathFormula}
              mode={mode}
              myTeam={myTeam}
              nextQuestionDelayCount={nextQuestionDelayCount}
              otherTeam={otherTeam}
              par={questionMetadata.par}
              paused={paused}
              penalty={penalty}
              playerSelf={playerSelf}
              players={players}
              playerThem={playerThem}
              profileId={profile.id}
              properNameShortAnswer={properNameShortAnswer}
              question={revealQuestion}
              questionImgUrl={questionImgUrl}
              questionIndex={questionIndex}
              ranked={ranked}
              rejoin={locationState?.rejoin}
              roundOver={roundOver}
              showConnectionStats={profile.settings?.connectionStats}
              showSnackbarMessage={showSnackbarMessage}
              socket={socket}
              spectateSocket={spectateSocket}
              spectate={locationState?.spectate}
              spectateGameId={spectateGameDataFromServer?.gameId}
              spectatorCount={spectatorCount}
              spectatorList={spectatorList}
              starterKit={starterKit}
              submittedByUsername={questionMetadata.submittedByUsername}
              stopSpectating={stopSpectating}
              survivalChallengeStatuses={survivalChallengeStatuses}
              survivalMode={survivalMode}
              teams={teams}
              timerLimitReached={timerLimitReached}
              totalNumQuestions={questionMetadata.questionSetCount}
              tournamentTotalPlayers={tournamentTotalPlayers}
              tournament={tournament}
              unrevealedIndex={unrevealedIndex}
              username={profile.username}
              variant={variant}
              willPause={willPause}
            />
          ) : gameId &&
            questionMetadata &&
            questionIndex !== undefined &&
            countdown === 0 &&
            profile &&
            !gameOver &&
            tournament &&
            mode === 'sit-n-go' ? (
            <TournamentGame
              answer={revealAnswer}
              category={questionMetadata.category}
              categorySet={categorySet}
              clearAfterPenalty={profile.settings?.clearAfterPenalty}
              cutoff={cutoff}
              delayBeforeQuestionRevealCount={delayBeforeQuestionRevealCount}
              displaySize={profile.settings?.displaySize}
              freezeGameClock={freezeGameClock}
              fullQuestion={revealFullQuestion}
              fuzzy={fuzzy}
              gameClock={gameClock}
              gameId={gameId}
              gameOver={gameOver}
              gameType={gameType}
              isAdmin={profile.admin}
              mathFormula={mathFormula}
              mode={mode}
              nextQuestionDelayCount={nextQuestionDelayCount}
              par={questionMetadata.par}
              penalty={penalty}
              players={players}
              playerSelf={playerSelf}
              profileId={profile.id}
              properNameShortAnswer={properNameShortAnswer}
              question={revealQuestion}
              questionImgUrl={questionImgUrl}
              questionIndex={questionIndex}
              rejoin={locationState?.rejoin}
              showConnectionStats={profile.settings?.connectionStats}
              socket={socket}
              spectate={locationState?.spectate}
              spectateGameId={spectateGameDataFromServer?.gameId}
              spectatorCount={spectatorCount}
              spectatorList={spectatorList}
              stopSpectating={stopSpectating}
              timerLimitReached={timerLimitReached}
              totalNumQuestions={questionMetadata.questionSetCount}
              tournamentTotalPlayers={tournamentTotalPlayers}
              tournament={tournament}
              unrevealedIndex={unrevealedIndex}
              username={profile.username}
            />
          ) : null}
          {gameId && questionSet && !gameOver ? <ScoreScreenLoading /> : null}
          {gameId && questionSet && (gameOver || roundOver) ? (
            <ScoreScreen
              averageDifficulty={averageDifficulty}
              bannedCategories={bannedCategories}
              categorySelections={categorySelections}
              categorySet={categorySet}
              cutoff={cutoff}
              daily={locationState?.daily}
              demons={demons}
              demonsVanquished={demonsVanquished}
              detailedUserAchievements={detailedUserAchievements}
              fromMain={locationState?.fromMain}
              fromSoloPunchcard={locationState?.fromSoloPunchcard}
              fromStarterKit={locationState?.fromStarterKit}
              gameId={gameId}
              gameOver={gameOver}
              gameType={gameType}
              handicap={handicap}
              handleJoinGameClick={handleJoinGameClick}
              handleLeaveGameClick={handleLeaveGameClick}
              handlePoolLeaderboardUsernameClick={
                handlePoolLeaderboardUsernameClick
              }
              isGhostChallenge={locationState?.ghostChallenge}
              isMatchMaking={locationState?.matchMaking}
              league={league}
              lobbyOpenGames={lobbyOpenGames}
              nextRoundStartTime={nextRoundStartTime}
              detailedPlayers={detailedPlayers}
              mode={mode}
              myTeam={myTeam}
              otherTeam={otherTeam}
              onCategoryBanClick={onCategoryBanClick}
              onUpdateProfile={onUpdateProfile}
              profile={profile}
              questionSet={questionSet}
              ranked={ranked}
              reportInaccuracy={reportInaccuracy}
              roundOver={roundOver}
              seedDetails={seedDetails}
              selectedPlayer={selectedPlayer}
              socket={socket}
              spectate={locationState?.spectate}
              spectatorCount={spectatorCount}
              spectatorList={spectatorList}
              starterKit={starterKit}
              survivalMode={survivalMode}
              tournament={locationState?.tournament}
              tournamentTotalPlayers={tournamentTotalPlayers}
              variant={variant}
            />
          ) : null}
        </div>
      ) : (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
          }}
        >
          <Typography variant="h5" sx={{ textAlign: 'center' }}>
            On mobile, PAR is best in Portrait mode.
          </Typography>
        </Box>
      )}
    </>
  );
};
